import { Component, DestroyRef, inject, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { ButtonComponent } from '@aprova-digital/design-system';
import { switchMap, tap, timer } from 'rxjs';

import { City } from '../../../domain/city/entities/city.entity';

import {
  StepperComponent,
  StepConfig,
  StepperEvent,
  IconComponent,
  LoadingBarComponent,
  ValidationErrorModalComponent,
  ValidationErrorData,
} from '../../../@ads/components';

import { Types } from '../../../domain/types.domain';
import { AuthRepositoryInstruction } from '../../../builder/instructions/repositories/auth.repository.instructions';
import { UserRepositoryInstruction } from '../../../builder/instructions/repositories/user.repository.instructions';
import { VerifyCreateAccountTokenServiceInstruction } from '../../../builder/instructions/services/verify-create-account-token.service.instruction';
import { CreateAccountServiceInstruction } from '../../../builder/instructions/services/create-account.service.instruction';
import { InitialCreateAccountInput } from '../../../domain/user/dtos/initial-create-account.input';

import { PersonalFormComponent } from './components/personal-form/personal-form.component';
import { AddressFormComponent } from './components/address-form/address-form.component';
import { AccountFormComponent } from './components/account-form/account-form.component';

import { AppHandler } from '../../app.handler';
import { RedirectService } from '../../redirect.service';
import { AccountFormHandler } from './account-form.handler';
import { PageTitleComponent } from "../../components/abstracts/page-title.component";

@Component({
  selector: 'app-create-account',
  standalone: true,
  imports: [
    PersonalFormComponent,
    AddressFormComponent,
    AccountFormComponent,
    ButtonComponent,
    StepperComponent,
    IconComponent,
    LoadingBarComponent,
    ValidationErrorModalComponent,
  ],
  providers: [
    AccountFormHandler,
    new AuthRepositoryInstruction(),
    new UserRepositoryInstruction(),
    new VerifyCreateAccountTokenServiceInstruction(),
    new CreateAccountServiceInstruction(),
  ],
  templateUrl: './create-account.component.html',
  styleUrl: './create-account.component.scss',
})
export class CreateAccountComponent extends PageTitleComponent('Criar conta') {
  private readonly _baseSteps: StepConfig[] = [
    { id: 'dados', title: 'Dados', enabled: true },
    { id: 'endereco', title: 'Endereço', enabled: true },
  ];

  @ViewChild(PersonalFormComponent)
  private _personalFormRef: PersonalFormComponent | undefined;

  private readonly _router = inject(Router);
  private readonly _route = inject(ActivatedRoute);

  private readonly _authRepository = inject(Types.AuthRepository);
  private readonly _verifyCreateAccountTokenService = inject(
    Types.VerifyCreateAccountTokenService
  );
  private readonly _createAccountService = inject(Types.CreateAccountService);

  private readonly _destroyRef = inject(DestroyRef);
  private readonly _appHandler = inject(AppHandler);
  private readonly _redirectService = inject(RedirectService);
  private readonly _accountFormHandler = inject(AccountFormHandler);

  public saving = signal(false);

  public steps: StepConfig[] = this.buildSteps(false);

  public cityEntity: City | undefined;
  public cityIdentity: string = this._route.snapshot.data['city']['identidade'];
  public cityConnection: string =
    this._route.snapshot.data['city']['connection'];
  public cityLoginUrl: string = '';
  public extraFields: FormlyFieldConfig[] = [];

  public hideRecaptcha: boolean = false;
  public hidePasswordFields: boolean = false;
  public initialAccountData?: InitialCreateAccountInput;
  public tokenId: string = '';
  public recaptchaReactive: string = '';
  public errorMessage = signal('');
  public hasError: boolean = false;

  public showWelcomeMessage = signal(true);
  public preRegisteredAccount = signal(false);

  public blockedFields: string[] = this.routeState['blockedFields'] ?? [];
  public isBusinessAccount = signal(false);
  public sendConfirmationEmail = signal(true);
  public accountCreated = signal(false);

  public error?: string = undefined;
  private verification?: {
    code: string;
    strategy: string;
    provider?: string;
  };

  // Validation error modal state
  public showValidationErrorModal = signal(false);
  public validationErrorData = signal<ValidationErrorData>({ type: 'generic' });
  public isValidatingCpfEmail = signal(false);

  get currentStep(): number {
    return this._accountFormHandler.getStep();
  }

  get routeState() {
    return this._router.lastSuccessfulNavigation?.extras?.state ?? {};
  }

  get isCurrentStepValid(): boolean {
    return this.steps[this.currentStep]?.completed ?? false;
  }

  ngOnInit(): void {
    this.setupHistoryRestoreGuard();

    const createAccountData = localStorage.getItem('createAccountData');
    if (createAccountData) {
      localStorage.removeItem('createAccountData');
      const createAccountDataParsed = JSON.parse(createAccountData);
      this._accountFormHandler.setProfileInfo(createAccountDataParsed);
      this._accountFormHandler.setAddressInfo(createAccountDataParsed);
    }

    this._appHandler.setBackButtonHome(false);
    this._appHandler.hideBackButton(true);

    this._route.data
      .pipe(
        tap(async ({ city }) => {
          if (!city.active) {
            this._appHandler.setLoading(false);
            this._appHandler.hideBackButton(true);
            this._appHandler.setError(true, {
              iconName: 'exclamation-outline',
              iconSize: '48px',
              message: 'Indisponível',
              description:
                'Consulte a organização para maiores esclarecimentos',
              options: {
                iconBackgroundColor: '#fff',
                messageClasses: 'font-bold',
                messageHorizontalAlign: 'center',
              },
            });
            return;
          }

          const config = await this._authRepository.getConfigByCityId(
            city.index
          );
          this.hideRecaptcha = config?.hideRecaptcha ?? false;
          this.hidePasswordFields = config?.hidePasswordFields ?? false;
          this.steps = this.buildSteps(this.hidePasswordFields);

          this.cityEntity = city;
          this.cityIdentity = city.identidade;
          this.cityConnection = city.connection;
          this.cityLoginUrl = city.loginUrl;
          this.extraFields = this.mountExtraFields(
            city.users?.extraFields ?? []
          );

          if (this.routeState) {
            if (this.routeState['blockedFields']) {
              this.blockedFields = this.routeState['blockedFields'];
            }

            if (this.routeState['verification']) {
              this.verification = this.routeState['verification'];
            }

            if (this.routeState['initialAccountData']) {
              this.initialAccountData = this.routeState['initialAccountData'];

              if (this.initialAccountData) {
                this._accountFormHandler.setProfileInfo({
                  type: this.initialAccountData.type ?? 'personal',
                  nome: this.initialAccountData.userMetadata?.firstName ?? '',
                  sobrenome:
                    this.initialAccountData.userMetadata?.lastName ?? '',
                  email: this.initialAccountData.email ?? '',
                  cpf: this.initialAccountData.userMetadata?.cpf ?? '',
                  telefone:
                    this.initialAccountData.userMetadata?.phoneNumber ?? '',
                });
                this._accountFormHandler.setAddressInfo({
                  cep: this.initialAccountData.userMetadata?.cep ?? '',
                  rua: this.initialAccountData.userMetadata?.rua ?? '',
                  numero: this.initialAccountData.userMetadata?.numero ?? '',
                  complemento:
                    this.initialAccountData.userMetadata?.complemento ?? '',
                  bairro: this.initialAccountData.userMetadata?.bairro ?? '',
                  cidade: this.initialAccountData.userMetadata?.cidade ?? '',
                  estado: this.initialAccountData.userMetadata?.estado ?? '',
                });
              }
            }
          }
        }),
        switchMap(() => this._route.queryParams),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: async (params) => {
          if (params?.['tokenId']) {
            this.tokenId = params['tokenId'];
          }

          const profileForm = this._accountFormHandler.getProfileInfo() ?? {};
          if (params?.['email']) {
            profileForm.email = params['email'];
          }

          if (params?.['name']) {
            const splittedName = params['name'].split(' ');
            profileForm.nome = splittedName.shift() ?? '';
            profileForm.sobrenome = splittedName.shift() ?? '';
          }

          if (params?.['cpf']) {
            profileForm.cpf = params['cpf'];
          }

          if (params?.['skipAccountConfirmation'] === 'true') {
            this.sendConfirmationEmail.set(false);
          }

          if (params?.['showWelcomeMessage'] === 'false') {
            this.showWelcomeMessage.set(false);
          }

          if (params?.['preRegisteredAccount'] === 'true') {
            this.preRegisteredAccount.set(true);
            this.sendConfirmationEmail.set(false);
          }

          if (params?.['tokenId']) {
            await this._verifyCreateAccountTokenService
              .handle({
                tokenId: this.tokenId,
              })
              .then((response) => {
                const fieldsToBlock: string[] = [];

                if (response.email && response.email.trim() !== '') {
                  fieldsToBlock.push('email');
                }

                if (response.cpf && response.cpf.trim() !== '') {
                  fieldsToBlock.push('cpf');
                }

                this.blockedFields = [...this.blockedFields, ...fieldsToBlock];

                const fullName = (response.firstName ?? '').trim();
                const nameParts = fullName.split(/\s+/);
                const firstName = nameParts.shift() ?? '';
                const lastName = nameParts.join(' ');

                this._accountFormHandler.setProfileInfo({
                  type: response.type ?? 'personal',
                  nome: firstName,
                  sobrenome: lastName,
                  email: response.email ?? '',
                  cpf: response.cpf ?? '',
                  telefone: response.phoneNumber ?? '',
                });

                this._appHandler.setLoading(false);
              })
              .catch((err: { error?: { code?: string }; status?: number; message?: string }) => {
                if (err.error?.code === 'inviteTokenInvalidOrExpired') {
                  this._appHandler.setError(true, {
                    message: 'Ops... Este link expirou.',
                    description:
                      'Para continuar, solicite um novo link com a pessoa ou organização responsável.',
                  });
                  this._appHandler.setLoading(false);
                  return;
                }

                this._appHandler.setError(true, {
                  message: 'A operação não pôde ser concluída.',
                  description:
                    'Por favor, revise seus dados antes de fazer uma nova tentativa. Se o problema persistir, entre em contato com nossa equipe de suporte.',
                  actionText: 'Tentar novamente',
                  action: () => {
                    this._appHandler.setLoading(true);
                    this._appHandler.clearError();
                  },
                });

                this._appHandler.setLoading(false);
              });
          } else {
            this._accountFormHandler.setProfileInfo(profileForm);
            this._appHandler.setLoading(false);
          }
        },
      });
  }

  private buildSteps(hidePasswordFields: boolean): StepConfig[] {
    if (hidePasswordFields) {
      return [...this._baseSteps];
    }

    return [
      ...this._baseSteps,
      { id: 'senha', title: 'Senha', enabled: true },
    ];
  }

  redirectToLogin(): void {
    if (this.cityLoginUrl) {
      window.location.href = this.cityLoginUrl;
      return;
    }

    this._router.navigate([`/${this.cityIdentity}`]);
  }

  startAccountCreation(): void {
    this.showWelcomeMessage.set(false);
    this._accountFormHandler.setStep(0);
  }

  goToStep(stepNumber: number): void {
    if (stepNumber < 0) {
      if (this.preRegisteredAccount()) {
        this.redirectToLogin();
        return;
      }

      this.showWelcomeMessage.set(true);
      return;
    }

    if (stepNumber > this.steps.length - 1) {
      this.createUser();
      return;
    }

    this._accountFormHandler.setStep(Math.max(stepNumber, 0));
  }

  setStepValidation(stepNumber: number, valid: boolean): void {
    if (this.steps[stepNumber]) {
      this.steps[stepNumber].completed = valid;
    }
  }

  onStepChange(event: StepperEvent): void {
    this._accountFormHandler.setStep(event.currentStep);
  }

  handleValidationError(errorData: ValidationErrorData): void {
    this.validationErrorData.set(errorData);
    this.showValidationErrorModal.set(true);
  }

  handleValidatingState(isValidating: boolean): void {
    this.isValidatingCpfEmail.set(isValidating);
    if (isValidating) {
      this._appHandler.setLoading(true, {
        title: 'Validando dados',
        description: 'Aguarde enquanto validamos seus dados',
      });
    } else {
      this._appHandler.setLoading(false);
    }
  }

  closeValidationErrorModal(): void {
    this.showValidationErrorModal.set(false);
  }

  retryAfterValidationError(): void {
    this.showValidationErrorModal.set(false);
    // User can edit their data and try again
  }

  async goToNextStep(): Promise<void> {
    // If on step 0, validate CPF and email first (skip for pre-registered accounts from invites)
    if (this.currentStep === 0 && this._personalFormRef && !this.preRegisteredAccount()) {
      const isValid = await this._personalFormRef.validateCpfAndEmail();
      if (!isValid) {
        return; // Validation failed, modal will be shown
      }
    }
    
    this.goToStep(this.currentStep + 1);
  }

  onChangeRecaptcha(recaptchaReactive: string): void {
    this.recaptchaReactive = recaptchaReactive;
  }

  createUser(): void {
    this.saving.set(true);

    this._createAccountService
      .handle({
        ...this._accountFormHandler.getCompleteInfo(),
        verifyMail: this.sendConfirmationEmail(),
        cityControl: {
          city: this.cityIdentity.slice(0, this.cityIdentity.length - 2),
          state: this.cityIdentity.slice(this.cityIdentity.length - 2),
        },
        connection: this.cityConnection,
        tokenId: this.tokenId,
        recaptchaReactive: this.recaptchaReactive,
        loginAfterInserted: !this.sendConfirmationEmail(),
        verification: this.verification,
      })
      .then((response) => {
        this.saving.set(false);
        this.accountCreated.set(true);

        if (!this.sendConfirmationEmail() && response.redirectUri) {
          timer(5000)
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe(() => {
              this._redirectService.postAuth({ redirectUri: response.redirectUri!, code: response.code });
            });
        }
      })
      .catch((err) => {
        this.saving.set(false);
        // salvar no localstorage para caso de retentativa de criar conta
        localStorage.setItem(
          'createAccountData',
          JSON.stringify(this._accountFormHandler.getCompleteInfo())
        );
        const statusCode = err.status || err.statusCode;
        const messageError = this.getMessageError(statusCode);
        const description = err.error?.message ?? err.message;

        this._appHandler.setError(true, {
          message: messageError,
          description: description,
          actionText: 'Revisar e tentar novamente',
          options: {
            iconPath: '/assets/icons/mail-alert-icon.svg',
            messageHorizontalAlign: 'center',
            messageClasses: 'font-bold',
            descriptionHorizontalAlign: 'center',
            descriptionClasses: 'font-normal',
          },
          action: () => {
            this._appHandler.clearError();
            this._router.navigate([`${this.cityIdentity}/create-account`], {
              queryParams: {
                skipAccountConfirmation: !this.sendConfirmationEmail(),
                showWelcomeMessage: false,
                preRegisteredAccount: this.preRegisteredAccount(),
              },
            });
          },
        });

        this.accountCreated.set(false);
      });
  }

  private setupHistoryRestoreGuard(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const handlePageShow = (event: PageTransitionEvent): void => {
      if (!event.persisted) {
        return;
      }

      this.resetSuccessState();
    };

    window.addEventListener('pageshow', handlePageShow);
    this._destroyRef.onDestroy(() => {
      window.removeEventListener('pageshow', handlePageShow);
    });
  }

  private resetSuccessState(): void {
    this.saving.set(false);
    this.accountCreated.set(false);
  }

  private getMessageError(statusCode: number): string {
    switch (statusCode) {
      case 410:
        return 'Seu convite está expirado!';
      case 422:
        return 'Convite já utilizado';
      case 498:
        return 'Token inválido!';
      case 500:
        return 'Erro interno do servidor';
      default:
        return 'Não foi possível criar a conta';
    }
  }

  // Função para montar os campos extras vindos do backend, separando em 2 colunas
  private mountExtraFields(
    extraFields: FormlyFieldConfig[]
  ): FormlyFieldConfig[] {
    const isOdd = extraFields.length % 2 !== 0;
    const newExtraFields: FormlyFieldConfig[] = [];

    for (let i = 0; i < extraFields.length; i++) {
      if (!extraFields[i]['className']) {
        extraFields[i]['className'] = 'w-full';
      } else if (!extraFields[i]['className']?.includes('w-full')) {
        extraFields[i]['className'] += ' w-full';
      }

      if (i > 0 && i % 2 !== 0) {
        newExtraFields.push({
          fieldGroupClassName: 'flex justify-between gap-x-6 w-full',
          fieldGroup: [extraFields[i - 1], extraFields[i]],
        });
      }

      if (isOdd && i === extraFields.length - 1) {
        newExtraFields.push({
          fieldGroupClassName: 'flex justify-between gap-x-6 w-full',
          fieldGroup: [
            extraFields[i],
            {
              type: 'input',
              key: 'empty',
              className: 'w-full opacity-0',
              props: {
                required: false,
              },
            },
          ],
        });
      }
    }

    return newExtraFields;
  }
}
